import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SelectChevron from '@/components/base/SelectChevron';
import TurnstileWidget, { type TurnstileWidgetHandle } from '@/components/feature/TurnstileWidget';
import { verifyTurnstileToken } from '@/lib/turnstile';

const FORM_URL = 'https://readdy.ai/api/form/da0m0o03256a3o8kouh0';
const MAX_MESSAGE_LENGTH = 500;

export default function ContactForm() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const tourParam = searchParams.get('tour') || '';
  const initialMessage = tourParam ? `${t('contact.interestedIn')} ${tourParam}. ` : '';

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [message, setMessage] = useState(initialMessage);
  const [charCount, setCharCount] = useState(initialMessage.length);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // Anti-spam: if the hidden field is filled, silently pretend success.
    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setStatus('success');
      setFormError('');
      form.reset();
      setMessage('');
      setCharCount(0);
      return;
    }

    if (!turnstileToken) {
      setStatus('error');
      setFormError(t('contact.captchaRequired'));
      return;
    }

    const captchaOk = await verifyTurnstileToken(turnstileToken);
    if (!captchaOk) {
      setStatus('error');
      setFormError(t('contact.captchaFailed'));
      turnstileRef.current?.reset();
      setTurnstileToken(null);
      return;
    }

    setStatus('loading');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key === 'website_alt') return;
        body.append(key, String(value));
      });

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam =
        typeof serverMsg === 'string' &&
        (serverMsg.toLowerCase().includes('spam') || serverMsg.toLowerCase().includes('form data is spam'));

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setFormError('');
        form.reset();
        setMessage('');
        setCharCount(0);
        turnstileRef.current?.reset();
        setTurnstileToken(null);
      } else {
        setStatus('error');
        setFormError(
          typeof serverMsg === 'string' && serverMsg.trim()
            ? serverMsg
            : t('contact.errorGeneric')
        );
      }
    } catch {
      setStatus('error');
      setFormError(t('contact.errorNetwork'));
    }
  };

  return (
    <div className="bg-white rounded-xl border border-secondary-200 p-6 md:p-8">
      <h2 className="font-heading text-xl md:text-2xl font-bold text-secondary-900 mb-2">
        {t('contact.formTitle')}
      </h2>
      <p className="text-sm text-secondary-500 mb-6">
        {t('contact.formSubtitle')}
      </p>

      <form onSubmit={handleSubmit} data-readdy-form noValidate>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="contact-name" className="block text-sm font-medium text-secondary-700 mb-1.5">
              {t('contact.fullName')} <span className="text-red-500">*</span>
            </label>
            <input
              id="contact-name"
              name="name"
              type="text"
              required
              placeholder={t('contact.yourName')}
              className="w-full rounded-lg border border-secondary-200 bg-white px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
            />
          </div>

          <div>
            <label htmlFor="contact-email" className="block text-sm font-medium text-secondary-700 mb-1.5">
              {t('contact.email')} <span className="text-red-500">*</span>
            </label>
            <input
              id="contact-email"
              name="email"
              type="email"
              required
              placeholder={t('contact.emailPlaceholder')}
              className="w-full rounded-lg border border-secondary-200 bg-white px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
            />
          </div>

          <div>
            <label htmlFor="contact-phone" className="block text-sm font-medium text-secondary-700 mb-1.5">
              {t('contact.phone')}
            </label>
            <input
              id="contact-phone"
              name="phone"
              type="tel"
              placeholder={t('contact.phonePlaceholder')}
              className="w-full rounded-lg border border-secondary-200 bg-white px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
            />
          </div>

          <div>
            <label htmlFor="contact-topic" className="block text-sm font-medium text-secondary-700 mb-1.5">
              {t('contact.topic')}
            </label>
            <div className="relative">
              <select
                id="contact-topic"
                name="topic"
                className="w-full appearance-none rounded-lg border border-secondary-200 bg-white px-3.5 py-2.5 text-sm text-secondary-800 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 cursor-pointer"
              >
                <option value="General inquiry">{t('contact.topicGeneral')}</option>
                <option value="Tailor-made tour">{t('contact.topicTailor')}</option>
                <option value="Existing booking">{t('contact.topicBooking')}</option>
                <option value="Partnership">{t('contact.topicPartnership')}</option>
                <option value="Feedback">{t('contact.topicFeedback')}</option>
              </select>
              <SelectChevron className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400" />
            </div>
          </div>
        </div>

        <div className="mt-4">
          <label htmlFor="contact-message" className="block text-sm font-medium text-secondary-700 mb-1.5">
            {t('contact.message')} <span className="text-red-500">*</span>
          </label>
          <textarea
            id="contact-message"
            name="message"
            required
            rows={5}
            maxLength={MAX_MESSAGE_LENGTH}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              setCharCount(e.target.value.length);
            }}
            placeholder={t('contact.messagePlaceholder')}
            className="w-full rounded-lg border border-secondary-200 bg-white px-3.5 py-2.5 text-sm text-secondary-800 placeholder:text-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 resize-none"
          />
          <div className="flex justify-end mt-1">
            <span className="text-xs text-secondary-400">
              {charCount}/{MAX_MESSAGE_LENGTH}
            </span>
          </div>
        </div>

        {/* Anti-spam honeypot — do not remove */}
        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="vts-field-check"
        />

        <TurnstileWidget ref={turnstileRef} onVerify={setTurnstileToken} />

        <button
          type="submit"
          disabled={status === 'loading'}
          className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-full transition-colors whitespace-nowrap"
        >
          {status === 'loading' ? (
            <>
              <i className="ri-loader-4-line animate-spin" />
              {t('contact.sending')}
            </>
          ) : (
            <>
              {t('contact.send')}
              <i className="ri-send-plane-line" />
            </>
          )}
        </button>

        {status === 'success' && (
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-green-50 border border-green-200 p-4">
            <i className="ri-checkbox-circle-line text-green-500 shrink-0" />
            <p className="text-sm text-green-700">
              {t('contact.success')}
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-4">
            <i className="ri-error-warning-line text-red-500 shrink-0" />
            <p className="text-sm text-red-700">{formError}</p>
          </div>
        )}
      </form>
    </div>
  );
}